import { z } from 'zod';
import {
    CardPointeVoidOutcome,
    type CardPointeVoidApprovedBody,
    type CardPointeVoidApprovedResponseDetails,
    type CardPointeVoidDeclinedBody,
    type CardPointeVoidDeclinedResponseDetails,
    type CardPointeVoidMalformedResponseDetails,
    type CardPointeVoidRequestBody,
    type CardPointeVoidRequestDetails,
    type CardPointeVoidResponseBody,
    type CardPointeVoidResponseDetails,
} from '../../../types/ipg/cardpointe/cardpointe-void.types';

export const CardPointeVoidAmountSchema: z.ZodString = z.string().regex(/^\d+(?:\.\d{2})?$/);

export const CardPointeVoidRequestBodySchema: z.ZodObject<{
    merchid: z.ZodString;
    retref: z.ZodString;
    amount: z.ZodOptional<z.ZodString>;
}> = z.object({
    merchid: z.string().min(1),
    retref: z.string().min(1),
    amount: CardPointeVoidAmountSchema.optional(),
}) satisfies z.ZodType<CardPointeVoidRequestBody>;

export const CardPointeVoidRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'POST'>;
    path: z.ZodLiteral<'/cardconnect/rest/void'>;
    body: typeof CardPointeVoidRequestBodySchema;
}> = z.object({
    method: z.literal('POST'),
    path: z.literal('/cardconnect/rest/void'),
    body: CardPointeVoidRequestBodySchema,
}) satisfies z.ZodType<CardPointeVoidRequestDetails>;

export const CardPointeVoidResponseBodySchema: z.ZodObject<{
    respstat: z.ZodUnion<readonly [z.ZodLiteral<'A'>, z.ZodLiteral<'B'>, z.ZodLiteral<'C'>]>;
    merchid: z.ZodOptional<z.ZodString>;
    orderId: z.ZodOptional<z.ZodString>;
    authcode: z.ZodOptional<z.ZodString>;
    amount: z.ZodOptional<z.ZodString>;
    currency: z.ZodOptional<z.ZodString>;
    retref: z.ZodOptional<z.ZodString>;
    respcode: z.ZodOptional<z.ZodString>;
    respproc: z.ZodOptional<z.ZodString>;
    resptext: z.ZodOptional<z.ZodString>;
    receipt: z.ZodOptional<z.ZodUnknown>;
    receiptObj: z.ZodOptional<z.ZodUnknown>;
    fee_amount: z.ZodOptional<z.ZodString>;
    fee_value: z.ZodOptional<z.ZodString>;
    fee_type: z.ZodOptional<
        z.ZodUnion<readonly [z.ZodLiteral<'SURCHRG'>, z.ZodLiteral<'SURCHRG_WAIVED'>]>
    >;
    fee_format: z.ZodOptional<z.ZodUnion<readonly [z.ZodLiteral<'percent'>, z.ZodLiteral<''>]>>;
    fee_authcode: z.ZodOptional<z.ZodString>;
    fee_merchid: z.ZodOptional<z.ZodString>;
    fee_retref: z.ZodOptional<z.ZodString>;
}> = z.object({
    respstat: z.union([z.literal('A'), z.literal('B'), z.literal('C')]),
    merchid: z.string().min(1).optional(),
    orderId: z.string().min(1).optional(),
    authcode: z.string().min(1).optional(),
    amount: z.string().min(1).optional(),
    currency: z.string().min(1).optional(),
    retref: z.string().min(1).optional(),
    respcode: z.string().min(1).optional(),
    respproc: z.string().min(1).optional(),
    resptext: z.string().min(1).optional(),
    receipt: z.unknown().optional(),
    receiptObj: z.unknown().optional(),
    fee_amount: z
        .string()
        .regex(/^\d+(?:\.\d{2})?$/)
        .optional(),
    fee_value: z.string().regex(/^\d+$/).optional(),
    fee_type: z.union([z.literal('SURCHRG'), z.literal('SURCHRG_WAIVED')]).optional(),
    fee_format: z.union([z.literal('percent'), z.literal('')]).optional(),
    fee_authcode: z.string().optional(),
    fee_merchid: z.string().optional(),
    fee_retref: z.string().optional(),
}) satisfies z.ZodType<CardPointeVoidResponseBody>;

export const CardPointeVoidApprovedBodySchema: z.ZodObject<{
    respstat: z.ZodLiteral<'A'>;
    merchid: z.ZodString;
    orderId: z.ZodOptional<z.ZodString>;
    authcode: z.ZodString;
    amount: z.ZodOptional<z.ZodString>;
    currency: z.ZodOptional<z.ZodString>;
    retref: z.ZodString;
    respcode: z.ZodOptional<z.ZodString>;
    respproc: z.ZodOptional<z.ZodString>;
    resptext: z.ZodOptional<z.ZodString>;
    receipt: z.ZodOptional<z.ZodUnknown>;
    receiptObj: z.ZodOptional<z.ZodUnknown>;
    fee_amount: z.ZodOptional<z.ZodString>;
    fee_value: z.ZodOptional<z.ZodString>;
    fee_type: z.ZodOptional<
        z.ZodUnion<readonly [z.ZodLiteral<'SURCHRG'>, z.ZodLiteral<'SURCHRG_WAIVED'>]>
    >;
    fee_format: z.ZodOptional<z.ZodUnion<readonly [z.ZodLiteral<'percent'>, z.ZodLiteral<''>]>>;
    fee_authcode: z.ZodOptional<z.ZodString>;
    fee_merchid: z.ZodOptional<z.ZodString>;
    fee_retref: z.ZodOptional<z.ZodString>;
}> = CardPointeVoidResponseBodySchema.extend({
    respstat: z.literal('A'),
    retref: z.string().min(1),
    merchid: z.string().min(1),
    authcode: z.string().min(1),
}) satisfies z.ZodType<CardPointeVoidApprovedBody>;

export const CardPointeVoidDeclinedBodySchema: z.ZodObject<{
    respstat: z.ZodUnion<readonly [z.ZodLiteral<'B'>, z.ZodLiteral<'C'>]>;
    merchid: z.ZodOptional<z.ZodString>;
    orderId: z.ZodOptional<z.ZodString>;
    authcode: z.ZodOptional<z.ZodString>;
    amount: z.ZodOptional<z.ZodString>;
    currency: z.ZodOptional<z.ZodString>;
    retref: z.ZodOptional<z.ZodString>;
    respcode: z.ZodOptional<z.ZodString>;
    respproc: z.ZodOptional<z.ZodString>;
    resptext: z.ZodString;
    receipt: z.ZodOptional<z.ZodUnknown>;
    receiptObj: z.ZodOptional<z.ZodUnknown>;
    fee_amount: z.ZodOptional<z.ZodString>;
    fee_value: z.ZodOptional<z.ZodString>;
    fee_type: z.ZodOptional<
        z.ZodUnion<readonly [z.ZodLiteral<'SURCHRG'>, z.ZodLiteral<'SURCHRG_WAIVED'>]>
    >;
    fee_format: z.ZodOptional<z.ZodUnion<readonly [z.ZodLiteral<'percent'>, z.ZodLiteral<''>]>>;
    fee_authcode: z.ZodOptional<z.ZodString>;
    fee_merchid: z.ZodOptional<z.ZodString>;
    fee_retref: z.ZodOptional<z.ZodString>;
}> = CardPointeVoidResponseBodySchema.extend({
    respstat: z.union([z.literal('B'), z.literal('C')]),
    resptext: z.string().min(1),
}) satisfies z.ZodType<CardPointeVoidDeclinedBody>;

export const CardPointeVoidApprovedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeVoidOutcome.APPROVED>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CardPointeVoidApprovedBodySchema;
}> = z.object({
    outcome: z.literal(CardPointeVoidOutcome.APPROVED),
    httpStatus: z.literal(200),
    body: CardPointeVoidApprovedBodySchema,
}) satisfies z.ZodType<CardPointeVoidApprovedResponseDetails>;

export const CardPointeVoidDeclinedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeVoidOutcome.DECLINED>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CardPointeVoidDeclinedBodySchema;
}> = z.object({
    outcome: z.literal(CardPointeVoidOutcome.DECLINED),
    httpStatus: z.literal(200),
    body: CardPointeVoidDeclinedBodySchema,
}) satisfies z.ZodType<CardPointeVoidDeclinedResponseDetails>;

export const CardPointeVoidMalformedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeVoidOutcome.MALFORMED_RESPONSE>;
    httpStatus: z.ZodNumber;
    body: z.ZodUnknown;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CardPointeVoidOutcome.MALFORMED_RESPONSE),
    httpStatus: z.number().int().positive(),
    body: z.unknown(),
    message: z.string().min(1),
}) satisfies z.ZodType<CardPointeVoidMalformedResponseDetails>;

export const CardPointeVoidResponseDetailsSchema: z.ZodType<CardPointeVoidResponseDetails> =
    z.discriminatedUnion('outcome', [
        CardPointeVoidApprovedResponseDetailsSchema,
        CardPointeVoidDeclinedResponseDetailsSchema,
        CardPointeVoidMalformedResponseDetailsSchema,
    ]);
